// DSR Admin — roles & permissions. Gated on settings.manage_rbac. List roles,
// create a role, and edit the permission matrix per role. The super_admin role
// is shown read-only (server rejects edits to it). Server also enforces the
// last-admin guardrail when a grant edit would remove the final manage_rbac
// holder.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { CreateRoleInput, Permission } from '@dsr/shared-types';
import {
  useRoles,
  useRole,
  usePermissions,
  useCreateRole,
  useUpdateRole,
  useSetRolePermissions,
  useDeleteRole,
} from '../api/queries/admin';
import { toast } from '../components/Toasts';
import { ApiError } from '../api/client';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  StatusBadge,
  Table,
} from '../components/ui';

const SUPER_ADMIN = 'super_admin';

export function AdminRolesPage(): JSX.Element {
  const { data, isLoading, isError, error } = useRoles();
  const roles = data?.roles ?? [];
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="page admin-roles-page">
      <header className="page-header">
        <div>
          <h1>Roles &amp; Permissions</h1>
          <p className="page-sub">Define roles and the permissions they grant.</p>
        </div>
        <div className="page-toolbar">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            New role
          </Button>
        </div>
      </header>

      {isLoading && <div className="page-state">Loading roles…</div>}
      {isError && (
        <div className="page-state page-error" role="alert">
          {error instanceof ApiError ? error.message : 'Failed to load roles.'}
        </div>
      )}

      {!isLoading && !isError && (
        <div className="table-wrap">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Role</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell>Permissions</Table.HeaderCell>
                <Table.HeaderCell>Users</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {roles.map((r) => (
                <Table.Row key={r.id}>
                  <Table.Cell tone="mono">
                    {r.name}
                    {r.name === SUPER_ADMIN && <StatusBadge status="new">protected</StatusBadge>}
                  </Table.Cell>
                  <Table.Cell>{r.description ?? '—'}</Table.Cell>
                  <Table.Cell>{r.permissions.length}</Table.Cell>
                  <Table.Cell>{r.user_count}</Table.Cell>
                  <Table.Cell className="table-actions">
                    <Button variant="ghost" className="btn-sm" onClick={() => setSelectedId(r.id)}>
                      {r.name === SUPER_ADMIN ? 'View' : 'Edit'}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
              {roles.length === 0 && (
                <Table.Empty colSpan={5}>No roles defined.</Table.Empty>
              )}
            </Table.Body>
          </Table>
        </div>
      )}

      {selectedId !== null && (
        <RoleEditor roleId={selectedId} onClose={() => setSelectedId(null)} />
      )}
      {createOpen && <CreateRoleModal onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

// ---------- Per-role permission matrix editor ----------

function RoleEditor({ roleId, onClose }: { roleId: number; onClose: () => void }): JSX.Element {
  const { data: roleData, isLoading } = useRole(roleId);
  const { data: permData } = usePermissions();
  const setPerms = useSetRolePermissions();
  const del = useDeleteRole();
  const update = useUpdateRole();

  const role = roleData?.role;
  const allPerms = permData?.permissions ?? [];
  const readOnly = role?.name === SUPER_ADMIN;

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [description, setDescription] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (role) {
      setSelected(new Set(role.permissions.map((p) => p.id)));
      setDescription(role.description ?? '');
    }
  }, [role]);

  // Group the catalog by resource prefix (the part before the dot).
  const groups = useMemo(() => {
    const m = new Map<string, Permission[]>();
    for (const p of allPerms) {
      const prefix = p.name.split('.')[0] ?? 'other';
      const arr = m.get(prefix) ?? [];
      arr.push(p);
      m.set(prefix, arr);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [allPerms]);

  const toggle = (id: number): void =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const onSavePerms = async (): Promise<void> => {
    try {
      await setPerms.mutateAsync({ id: roleId, permission_ids: [...selected] });
      toast.success('Permissions updated.');
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to update permissions.');
    }
  };

  const onSaveMeta = async (): Promise<void> => {
    try {
      await update.mutateAsync({ id: roleId, description: description.trim() || null });
      toast.success('Role updated.');
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to update role.');
    }
  };

  const onDelete = async (): Promise<void> => {
    try {
      await del.mutateAsync(roleId);
      toast.success('Role deleted.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Delete failed.');
      setConfirmDelete(false);
    }
  };

  return (
    <Dialog
      title={role ? `Role: ${role.name}` : 'Role'}
      open
      onClose={onClose}
      busy={setPerms.isPending || del.isPending || update.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {!readOnly && (
            <Button variant="primary" disabled={setPerms.isPending} onClick={() => void onSavePerms()}>
              {setPerms.isPending ? 'Saving…' : 'Save permissions'}
            </Button>
          )}
        </>
      }
    >
      {isLoading || !role ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="role-editor">
          {readOnly && (
            <p className="muted">The super_admin role holds every permission and cannot be edited.</p>
          )}

          {!readOnly && (
            <div className="form-grid">
              <FormField label="Description" full>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} />
              </FormField>
              <div className="settings-actions">
                {/* No primitive variant maps to `.btn-secondary`; keep the native
                    button so the existing color is preserved exactly. */}
                <button type="button" className="btn btn-secondary btn-sm" disabled={update.isPending} onClick={() => void onSaveMeta()}>
                  Save description
                </button>
                <Button variant="danger" className="btn-sm" disabled={del.isPending} onClick={() => setConfirmDelete(true)}>
                  Delete role
                </Button>
              </div>
            </div>
          )}

          <div className="perm-matrix">
            {groups.map(([prefix, perms]) => (
              <fieldset key={prefix} className="perm-group">
                <legend className="mono">{prefix}</legend>
                <div className="checkbox-list">
                  {perms.map((p) => (
                    <label key={p.id} className="checkbox-field" title={p.description ?? undefined}>
                      <input
                        type="checkbox"
                        checked={selected.has(p.id)}
                        disabled={readOnly}
                        onChange={() => toggle(p.id)}
                      />
                      <span className="mono">{p.name}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete role"
        message="Delete this role? It must not be assigned to any users."
        confirmLabel="Delete"
        danger
        busy={del.isPending}
        onConfirm={() => void onDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
    </Dialog>
  );
}

// ---------- Create role ----------

function CreateRoleModal({ onClose }: { onClose: () => void }): JSX.Element {
  const create = useCreateRole();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const input: CreateRoleInput = {
      name: name.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
    };
    try {
      await create.mutateAsync(input);
      toast.success('Role created.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to create role.');
    }
  };

  return (
    <Dialog
      title="New role"
      open
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>Cancel</Button>
          <Button type="submit" form="create-role-form" variant="primary" disabled={create.isPending || !name.trim()}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="create-role-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Name" required>
          <Input
            className="mono"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. regional_manager"
            required
            autoFocus
          />
        </FormField>
        <FormField label="Description" full>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </FormField>
        <p className="muted small">Lowercase letters, digits, and underscores only.</p>
      </form>
    </Dialog>
  );
}
